// Who is on an agent, as p5_member_views_contract.md has it.
//
// - Everyone sees the list: the owner first, then the members, a suspended one marked so. Each
//   person sets their own email (the switch on their own row: the list gives email_ok on the
//   caller's entry alone).
// - The owner adds people and removes members (a suspended one too: that clears them from the
//   list). The server adds only someone who can already see everything the agent works with, so
//   the search checks each person it shows (at most 10) and greys out, with the server's reason,
//   whoever can't be added. A member who isn't the owner can leave.
// - The members are also the members of the agent's Messages thread, and the dialog says so.
//
// Errors are the server's sentences, through agentErrorText. After any 409 the list and the agent
// are read again (onChanged), then the sentence shows. After a 404 (the caller is no longer an
// active member) the agent is read again; Leave then has nothing left to do, so it counts as done.
// A 503 is a passing failure: nothing was changed, and the same button tries again.
//
// Focus stays inside the dialog while it is open, Escape closes it, and focus goes back to what
// opened it (useDialogFocus).
//
// Props: agentId; isOwner; schema (whose colleagues the search looks through); title (the
// agent's, for the note about its Messages thread); onClose; onChanged (the agent changed: read
// it again); onLeft (the caller left the agent: close it, and read the list again). Without
// onLeft, leaving calls onChanged and then onClose. readOnly (a view-as session): the list only,
// with no email switch, Remove, Add people or Leave, and a line that says why.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { addMember, checkMembers, leaveAgent, listMembers, removeMember, setAgentEmail } from './agentsService';
import { searchColleagues } from '../../../services/messagingService';
import { agentErrorCanRetry, agentErrorText } from './agentErrors';
import useDialogFocus from './useDialogFocus';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const DOT = String.fromCharCode(0xb7);
const CROSS = String.fromCharCode(0x2715);
const ROBOT = String.fromCodePoint(0x1f916);
// The most people one search shows, and so checks (the members check takes at most 25).
const SHOWN = 10;
// The reason for someone the members check didn't answer for: they can't be added from here.
const UNCHECKED = "Couldn't check them just now. Search again.";

const SCRIM = 'fixed inset-0 z-[70] bg-black/40 dark:bg-black/60 flex items-center justify-center p-4';
const DIALOG = 'w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-white dark:bg-[#0f0f16] border border-slate-200 dark:border-slate-800 p-4 space-y-3 shadow-2xl outline-none';
const TITLE = 'text-sm font-bold text-slate-900 dark:text-slate-100';
const CLOSE = 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300';
const LIST = 'list-none m-0 p-0';
const ROW = 'flex items-center justify-between gap-2 text-xs text-slate-700 dark:text-slate-200';
const QUIET = 'text-slate-400 dark:text-slate-500';
const NOTE = 'text-[11px] text-slate-500 dark:text-slate-400';
const FIELD = 'flex-1 min-w-0 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-800 dark:text-slate-100';
const BUTTON = 'rounded-md px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50';
const SEARCH = 'bg-blue-600 hover:bg-blue-500 text-white';
const ADD = 'bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-500 dark:hover:bg-emerald-500 text-white';
const REMOVE = 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10';
const LEAVE = 'text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10';
const ERROR = 'flex items-center justify-between gap-2 text-[11px] text-red-600 dark:text-red-400';
const RETRY = 'text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10';

const label = (person) => person.name || person.email || 'Someone';
const sameId = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// The caller's own on/off email setting.
function EmailSwitch({ on, disabled, onToggle }) {
  return (
    <button type="button" aria-pressed={on} disabled={disabled} onClick={onToggle}
            title="Email me when a run finishes or needs someone. No patient details in the email."
            className="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400 disabled:opacity-50">
      Email me
      <span aria-hidden="true"
            className={`relative inline-flex h-4 w-7 rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white dark:bg-white shadow transition-all ${on ? 'left-3.5' : 'left-0.5'}`} />
      </span>
    </button>
  );
}

export default function MembersDialog({ agentId, isOwner = false, schema = null, title = '', onClose, onChanged, onLeft,
  readOnly = false }) {
  const [members, setMembers] = useState(null); // null until the list first comes
  const [error, setError] = useState(null); // {text, retry}: what went wrong, and whether reading again may help
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null before a search has answered
  const [working, setWorking] = useState({}); // what waits for its answer: 'email', 'leave', 'add:<id>', 'remove:<id>'
  const [confirmLeave, setConfirmLeave] = useState(false);
  const waiting = useRef(new Set());
  const searchingNow = useRef(false);
  const dialog = useRef(null);

  const load = useCallback(async () => {
    try {
      const answer = await listMembers(agentId);
      setMembers(Array.isArray(answer?.members) ? answer.members : []);
      setError(null);
    } catch (err) {
      setError({ text: agentErrorText(err), retry: agentErrorCanRetry(err) });
    }
  }, [agentId]);
  useEffect(() => { load(); }, [load]);
  useDialogFocus(dialog, onClose);

  // One request at a time for each thing: a second click while one waits sends nothing.
  const claim = (key) => {
    if (waiting.current.has(key)) return false;
    waiting.current.add(key);
    setWorking((now) => ({ ...now, [key]: true }));
    setError(null);
    return true;
  };
  const release = (key) => {
    waiting.current.delete(key);
    setWorking((now) => {
      const next = { ...now };
      delete next[key];
      return next;
    });
  };

  const changed = async () => {
    await load();
    onChanged?.();
  };
  // After a 409, the list and the agent first; then the sentence. After a 404 the list can't be
  // read either: the agent is read again.
  const refused = async (err) => {
    if (err?.status === 409) await changed();
    else if (err?.status === 404) onChanged?.();
    setError({ text: agentErrorText(err), retry: false });
  };

  const search = async (event) => {
    event?.preventDefault();
    const words = query.trim();
    if (!words || searchingNow.current) return;
    searchingNow.current = true;
    setSearching(true);
    setError(null);
    setResults(null);
    try {
      const found = await searchColleagues(words, schema);
      const people = (Array.isArray(found?.users) ? found.users : [])
        .filter((person, index, all) => person && person.id && all.findIndex((other) => other && sameId(other.id, person.id)) === index)
        .slice(0, SHOWN);
      let checked = [];
      if (people.length) {
        const answer = await checkMembers(agentId, people.map((person) => person.id));
        checked = Array.isArray(answer?.results) ? answer.results : [];
      }
      setResults(people.map((person) => {
        const result = checked.find((row) => row && sameId(row.user_id, person.id));
        if (!result) return { ...person, ok: false, reason: UNCHECKED };
        return { ...person, ok: result.ok === true, reason: result.ok === true ? '' : result.reason || UNCHECKED };
      }));
    } catch (err) {
      setError({ text: agentErrorText(err), retry: false });
    } finally {
      searchingNow.current = false;
      setSearching(false);
    }
  };

  // Sends `request` once for `key`; the button is free again as soon as it is answered. Returns its
  // answer, or undefined once a refusal is shown. `done404`: a 404 means it has nothing left to do,
  // so it counts as answered.
  const send = async (key, request, { done404 = false } = {}) => {
    if (!claim(key)) return undefined;
    let answer;
    try {
      answer = await request();
    } catch (err) {
      release(key);
      if (done404 && err?.status === 404) return {};
      await refused(err);
      return undefined;
    }
    release(key);
    return answer ?? {};
  };

  const add = async (person) => {
    if (!(await send(`add:${person.id}`, () => addMember(agentId, person.id)))) return;
    setResults((now) => (now || []).filter((other) => other.id !== person.id));
    await changed();
  };

  const remove = async (member) => {
    if (!(await send(`remove:${member.user_id}`, () => removeMember(agentId, member.user_id)))) return;
    await changed();
  };

  const toggleEmail = async (member) => {
    const answer = await send('email', () => setAgentEmail(agentId, member.email_ok !== true));
    if (!answer) return;
    const saved = answer.email_ok === true;
    setMembers((now) => (now || []).map((row) => (row.user_id === member.user_id ? { ...row, email_ok: saved } : row)));
    onChanged?.();
  };

  const leave = async () => {
    if (!confirmLeave) {
      setConfirmLeave(true);
      return;
    }
    if (waiting.current.has('leave')) return;
    // A 404: they were removed or suspended meanwhile, so they are off the agent already.
    if (!(await send('leave', () => leaveAgent(agentId), { done404: true }))) {
      setConfirmLeave(false);
      return;
    }
    if (onLeft) {
      onLeft();
    } else {
      onChanged?.();
      onClose?.();
    }
  };

  const thread = title ? `${ROBOT} ${title}` : "this agent's thread";
  const threadNote = isOwner && !readOnly
    ? `These are also the members of ${thread} in Messages: adding or removing someone here adds them to the thread or takes them out.`
    : `These are also the members of ${thread} in Messages.`;

  return (
    <div className={SCRIM}>
      <div ref={dialog} role="dialog" aria-modal="true" aria-label="Agent members" tabIndex={-1} className={DIALOG}>
        <div className="flex items-center justify-between">
          <div className={TITLE}>Members</div>
          <button type="button" onClick={onClose} className={CLOSE} aria-label="Close">{CROSS}</button>
        </div>
        {members === null && !error && <div className={NOTE}>{`Loading${ELLIPSIS}`}</div>}
        {members !== null && (
          <>
            <ul className={`${LIST} space-y-1.5`}>
              {members.map((member) => {
                const own = !readOnly && Object.prototype.hasOwnProperty.call(member, 'email_ok');
                const removable = !readOnly && isOwner && member.role !== 'owner';
                const name = member.name || 'A member';
                const standing = `${DOT} ${member.role || 'member'}${member.status && member.status !== 'active' ? ` ${DOT} ${member.status}` : ''}`;
                return (
                  <li key={member.user_id} className={`${ROW}${own || removable ? '' : ' py-1'}`}>
                    <span className="min-w-0 break-words">{name} <span className={QUIET}>{standing}</span></span>
                    {own && (
                      <EmailSwitch on={member.email_ok === true} disabled={!!working.email} onToggle={() => toggleEmail(member)} />
                    )}
                    {!own && removable && (
                      <button type="button" aria-label={`Remove ${name}`} disabled={!!working[`remove:${member.user_id}`]}
                              onClick={() => remove(member)} className={`${BUTTON} shrink-0 ${REMOVE}`}>
                        Remove
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className={NOTE}>{threadNote}</div>
            {readOnly && <div className={NOTE}>{"You're viewing as someone else, so you can't change who is on it."}</div>}
            {readOnly ? null : isOwner ? (
              <div className="space-y-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                <div className="pt-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">Add people</div>
                <form className="flex gap-2" onSubmit={search}>
                  <input aria-label="Find a colleague" className={FIELD} value={query} onChange={(e) => setQuery(e.target.value)} />
                  <button type="submit" disabled={searching || !query.trim()} className={`${BUTTON} shrink-0 ${SEARCH}`}>
                    {searching ? `Searching${ELLIPSIS}` : 'Search'}
                  </button>
                </form>
                {results && results.length > 0 && (
                  <ul className={`${LIST} space-y-1`}>
                    {results.map((person) => (
                      <li key={person.id} className="text-xs flex items-center justify-between gap-2">
                        <span className={`min-w-0 break-words ${person.ok ? 'text-slate-700 dark:text-slate-200' : QUIET}`}>{label(person)}</span>
                        {person.ok ? (
                          <button type="button" aria-label={`Add ${label(person)}`} disabled={!!working[`add:${person.id}`]}
                                  onClick={() => add(person)} className={`${BUTTON} shrink-0 ${ADD}`}>
                            Add
                          </button>
                        ) : (
                          <span className={`text-[11px] text-right ${QUIET}`}>{person.reason}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {results && results.length === 0 && <div className={NOTE}>No one found.</div>}
              </div>
            ) : (
              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                <span className={NOTE}>{"You'll stop seeing this agent and its Messages thread."}</span>
                <button type="button" disabled={!!working.leave} onClick={leave} className={`${BUTTON} shrink-0 ${LEAVE}`}>
                  {confirmLeave ? 'Really leave?' : 'Leave'}
                </button>
              </div>
            )}
          </>
        )}
        {error && (
          <div role="alert" className={ERROR}>
            <span>{error.text}</span>
            {error.retry && (
              <button type="button" onClick={() => { setError(null); load(); }} className={`${BUTTON} shrink-0 ${RETRY}`}>Try again</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
